import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Coffee,
  Heart,
  IceCream,
  MapPin,
  Pizza,
  Sandwich,
  Search,
  ShoppingBag,
  Star,
  Utensils,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useRef, useState } from "react";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import {
  fetchAppDownloadSection,
  fetchFeaturedRestaurants,
  fetchPromotionalOffers,
} from "@/services/landing/hareengEatsService";
import { fetchHeroBackgrounds } from "@/services/landing/heroBackgroundService";

export default function Eats() {
  // App Download Section Content
  const [appDownloadContent, setAppDownloadContent] = useState<null | {
    androidUrl?: string;
    backgroundStyle?: string;
    description: string;
    heading: string;
    imageAlt?: string;
    imageUrl?: string;
    iosUrl?: string;
  }>(null);
  const [heroBackground, setHeroBackground] = useState<null | {
    imageAlt: string;
    imageUrl: string;
  }>(null);
  const [heroLoading, setHeroLoading] = useState(true);

  useEffect(() => {
    fetchAppDownloadSection?.().then((data) => {
      if (data) {
        setAppDownloadContent({
          androidUrl: data.androidUrl,
          backgroundStyle: data.backgroundColor,
          description:
            data.description ||
            "Order faster, track in real-time, and unlock exclusive app-only deals.",
          heading: data.title || "Get the Hareeng Eats App",
          imageAlt: data.phoneImageAlt,
          imageUrl: data.phoneImageUrl,
          iosUrl: data.iosUrl,
        });
      }
    });
    fetchHeroBackgrounds("eats").then((data) => {
      setHeroBackground(data);
      setHeroLoading(false);
    });
  }, []);
  const cuisineTypes = [
    {
      icon: <Sandwich className="text-primary" size={24} />,
      name: "Fast Food",
    },
    {
      icon: <Pizza className="text-warning" size={24} />,
      name: "Pizza",
    },
    {
      icon: <Coffee className="text-accent" size={24} />,
      name: "Coffee",
    },
    {
      icon: <IceCream className="text-secondary" size={24} />,
      name: "Desserts",
    },
    {
      icon: <Utensils className="text-success" size={24} />,
      name: "Local Food",
    },
    {
      icon: <Heart className="text-info" size={24} />,
      name: "Healthy",
    },
  ];

  // Replace static featuredRestaurants with API data
  const [featuredRestaurants, setFeaturedRestaurants] = useState<any[]>([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedRestaurants().then((data) => {
      setFeaturedRestaurants(Array.isArray(data) ? data : []);
      setFeaturedLoading(false);
    });
  }, []);

  const features = [
    {
      description: "Average delivery time under 30 minutes",
      icon: <Clock className="text-primary" size={32} />,
      title: "Fast Delivery",
    },
    {
      description: "Curated selection of the best local eateries",
      icon: <Star className="text-warning" size={32} />,
      title: "Top Restaurants",
    },
    {
      description: "Track your order from kitchen to your door",
      icon: <MapPin className="text-success" size={32} />,
      title: "Real-time Tracking",
    },
    {
      description: "Simple app interface for quick food ordering",
      icon: <ShoppingBag className="text-secondary" size={32} />,
      title: "Easy Ordering",
    },
  ];

  // Add theme adaptive QR code state
  const [showQR, setShowQR] = useState<"android" | "ios" | null>(null);
  const [isDark, setIsDark] = useState(
    document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributeFilter: ["class"],
      attributes: true,
    });
    return () => observer.disconnect();
  }, []);

  const iosAppUrl = "https://apps.apple.com/app/idYOUR_APP_ID";
  const androidAppUrl =
    "https://play.google.com/store/apps/details?id=YOUR_PACKAGE_NAME";

  // Responsive slider state for featured restaurants
  const [slideIndex, setSlideIndex] = useState(0);

  const getItemsPerView = () => {
    if (window.innerWidth < 640) return 1; // mobile
    if (window.innerWidth < 1024) return 2; // tablet
    if (window.innerWidth < 1280) return 3; // small desktop
    return 4; // large desktop
  };

  const [itemsPerView, setItemsPerView] = useState(getItemsPerView());

  useEffect(() => {
    const handleResize = () => setItemsPerView(getItemsPerView());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const maxIndex = Math.max(0, featuredRestaurants.length - itemsPerView);

  const handlePrev = () => setSlideIndex((i) => Math.max(i - 1, 0));
  const handleNext = () => setSlideIndex((i) => Math.min(i + 1, maxIndex));

  // Replace static promotions with API data
  const [promotions, setPromotions] = useState<any[]>([]);
  const [promotionsLoading, setPromotionsLoading] = useState(true);
  const [promoSlideIndex, setPromoSlideIndex] = useState(0);
  const [promotionsSlidesPerView, setPromotionsSlidesPerView] = useState(1);
  const promoSliderRef = useRef<HTMLDivElement>(null);
  const [promoMoreVisible, setPromoMoreVisible] = useState({
    next: false,
    prev: false,
  });

  useEffect(() => {
    fetchPromotionalOffers().then((data) => {
      setPromotions(Array.isArray(data) ? data.filter((o) => o.isActive) : []);
      setPromotionsLoading(false);
    });
  }, []);

  // Responsive promotions slides per view
  useEffect(() => {
    const calcSlides = () => {
      if (window.innerWidth >= 1024) return setPromotionsSlidesPerView(4);
      if (window.innerWidth >= 640) return setPromotionsSlidesPerView(2);
      setPromotionsSlidesPerView(1);
    };
    calcSlides();
    window.addEventListener("resize", calcSlides);
    return () => window.removeEventListener("resize", calcSlides);
  }, []);

  // Track scroll position for promotion slider arrows
  useEffect(() => {
    const el = promoSliderRef.current;
    if (!el) return;
    const updateArrows = () => {
      setPromoMoreVisible({
        next: el.scrollLeft + el.offsetWidth < el.scrollWidth - 1,
        prev: el.scrollLeft > 0,
      });
    };
    el.addEventListener("scroll", updateArrows);
    updateArrows();
    return () => el.removeEventListener("scroll", updateArrows);
  }, [promotions.length, promotionsSlidesPerView]);

  // Split promotions into grid and slider
  const gridPromotions = promotions.slice(0, promotionsSlidesPerView);
  const sliderPromotions = promotions.slice(promotionsSlidesPerView);

  // Scroll handler for promotions slider
  const scrollPromo = (dir: "left" | "right") => {
    if (!promoSliderRef.current) return;
    const card = promoSliderRef.current.querySelector(".promo-card");
    if (!card) return;
    const cardWidth = (card as HTMLElement).offsetWidth + 24;
    promoSliderRef.current.scrollBy({
      behavior: "smooth",
      left: dir === "left" ? -cardWidth : cardWidth,
    });
  };

  if (
    typeof document !== "undefined" &&
    !document.getElementById("hero-shimmer-style")
  ) {
    const style = document.createElement("style");
    style.id = "hero-shimmer-style";
    style.innerHTML = `
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .animate-shimmer {
        animation: shimmer 2s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }

  return (
    <>
      <AppNavbar />

      <div className="min-h-screen bg-base-200">
        <Breadcrumbs />

        {/* Hero Section */}
        <section className="relative -mt-16 overflow-hidden pt-16">
          <div className="absolute inset-0 opacity-30">
            {heroLoading ? (
              <div className="relative h-full w-full overflow-hidden bg-base-200">
                <div
                  className="animate-shimmer absolute inset-0 bg-gradient-to-r from-base-200 via-base-100 to-base-200"
                  style={{ backgroundSize: "200% 100%" }}
                />
              </div>
            ) : heroBackground?.imageUrl ? (
              <img
                alt={heroBackground?.imageAlt || "Hero background"}
                className="h-full w-full object-cover"
                src={heroBackground.imageUrl}
              />
            ) : null}
          </div>

          <div className="relative mx-auto max-w-7xl px-4 py-24">
            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div>
                <h1 className="mb-6 text-5xl font-bold leading-tight lg:text-6xl">
                  Craving something{" "}
                  <span className="text-warning">delicious?</span>
                </h1>

                <p className="mb-8 text-xl opacity-90">
                  Get your favorite meals delivered fast from the best
                  restaurants in your city. From local gems to popular chains.
                </p>
              </div>

              <div className="relative">
                <div className="relative flex min-h-[300px] items-center justify-center text-center">
                  <img
                    alt={appDownloadContent?.imageAlt || "Mobile app interface"}
                    className="relative z-10 mx-auto h-80 w-auto max-w-[90%] rounded-2xl object-cover shadow-2xl"
                    src={
                      appDownloadContent?.imageUrl ||
                      "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=300&h=400&fit=crop&crop=center"
                    }
                    style={{
                      boxShadow: "0 24px 64px 0 rgba(0,0,0,0.18)",
                      transform: "perspective(900px) rotateX(18deg) scale(1.1)",
                    }}
                  />
                  <div
                    className="absolute bottom-8 left-1/2 h-8 w-72 -translate-x-1/2 rounded-full bg-base-200 opacity-60 blur-md"
                    style={{
                      zIndex: 5,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Cuisine Categories */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">What Are You Craving?</h2>
              <p className="text-xl text-base-content/70">
                Explore cuisines from around the world
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
              {cuisineTypes.map((cuisine, index) => (
                <div
                  className="card cursor-pointer bg-base-100 shadow-lg transition-shadow hover:shadow-xl"
                  key={index}
                >
                  <div className="card-body items-center p-6 text-center">
                    <div className="mb-3">{cuisine.icon}</div>
                    <h3 className="font-semibold">{cuisine.name}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Promotions */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Today's Deals</h2>
              <p className="text-xl text-base-content/70">
                Save more with our exclusive offers
              </p>
            </div>
            {promotionsLoading ? (
              <div className="text-center">Loading...</div>
            ) : promotions.length === 0 ? (
              <div className="text-center">No offers found.</div>
            ) : (
              <>
                {/* Grid: First promotionsSlidesPerView items */}
                <div
                  className={`grid grid-cols-1 gap-6 ${
                    promotionsSlidesPerView >= 2 ? "sm:grid-cols-2" : ""
                  } ${promotionsSlidesPerView >= 4 ? "md:grid-cols-4" : ""}`}
                >
                  {gridPromotions.map((promo, index) => (
                    <div
                      className="promo-card card bg-gradient-to-br from-primary to-secondary text-primary-content shadow-xl transition-transform hover:scale-105"
                      key={index}
                      style={{ background: promo.backgroundColor }}
                    >
                      <figure className="h-32">
                        <img
                          alt={promo.title}
                          className="h-full w-full object-cover opacity-70"
                          src={promo.imageUrl}
                        />
                      </figure>
                      <div className="card-body">
                        <h3 className="card-title text-lg font-bold">
                          {promo.title}
                        </h3>
                        <p className="text-sm opacity-90">{promo.subtitle}</p>
                        <p className="line-clamp-2 text-sm opacity-90">
                          {promo.description}
                        </p>
                        <div className="card-actions mt-4 items-center justify-between">
                          <code className="rounded bg-primary-content px-2 py-1 text-sm font-bold text-primary">
                            {promo.promoCode}
                          </code>
                          <button className="btn btn-accent btn-sm">
                            Use Code
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Slider: Remaining promotions */}
                {sliderPromotions.length > 0 && (
                  <div className="mt-8">
                    <div
                      className="flex gap-6 overflow-x-auto scroll-smooth pb-2"
                      ref={promoSliderRef}
                      style={{ scrollbarWidth: "none" }}
                    >
                      {sliderPromotions.map((promo, index) => (
                        <div
                          className="promo-card flex min-w-[280px] max-w-sm flex-shrink-0 flex-col rounded-xl bg-gradient-to-br from-primary to-secondary p-4 text-primary-content shadow-md transition-transform hover:scale-105"
                          key={index}
                          style={{ background: promo.backgroundColor }}
                        >
                          <figure className="mb-3 h-32 overflow-hidden rounded-lg">
                            <img
                              alt={promo.title}
                              className="h-full w-full object-cover opacity-70"
                              src={promo.imageUrl}
                            />
                          </figure>
                          <h3 className="mb-1 text-base font-bold">
                            {promo.title}
                          </h3>
                          <p className="mb-2 text-sm opacity-90">
                            {promo.subtitle}
                          </p>
                          <p className="line-clamp-2 flex-grow text-sm opacity-90">
                            {promo.description}
                          </p>
                          <div className="card-actions mt-3 items-center justify-between">
                            <code className="rounded bg-primary-content px-2 py-1 text-xs font-bold text-primary">
                              {promo.promoCode}
                            </code>
                            <button className="btn btn-accent btn-xs">
                              Use Code
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Arrows below slider */}
                    <div className="mt-4 flex justify-center gap-4">
                      {promoMoreVisible.prev && (
                        <button
                          aria-label="Previous"
                          className="rounded-full bg-base-100 p-2 shadow hover:bg-primary hover:text-white"
                          onClick={() => scrollPromo("left")}
                          type="button"
                        >
                          <ChevronLeft size={28} />
                        </button>
                      )}
                      {promoMoreVisible.next && (
                        <button
                          aria-label="Next"
                          className="rounded-full bg-base-100 p-2 shadow hover:bg-primary hover:text-white"
                          onClick={() => scrollPromo("right")}
                          type="button"
                        >
                          <ChevronRight size={28} />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Featured Restaurants Slider */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Featured Restaurants</h2>
              <p className="text-xl text-base-content/70">
                Popular choices in your area
              </p>
            </div>
            {featuredLoading ? (
              <div className="col-span-3 text-center">Loading...</div>
            ) : featuredRestaurants.length === 0 ? (
              <div className="col-span-3 text-center">
                No featured restaurants found.
              </div>
            ) : (
              <div className="relative">
                <div
                  className="flex gap-8 transition-all duration-300"
                  style={{
                    minHeight: 340,
                  }}
                >
                  {featuredRestaurants
                    .slice(slideIndex, slideIndex + itemsPerView)
                    .map((restaurant, index) => (
                      <div
                        className="card min-w-0 flex-1 bg-base-100 shadow-lg transition-shadow hover:shadow-xl"
                        key={restaurant.id || index}
                        style={{
                          maxWidth:
                            itemsPerView === 1
                              ? "100%"
                              : itemsPerView === 2
                                ? "50%"
                                : itemsPerView === 3
                                  ? "33.3333%"
                                  : "25%",
                        }}
                      >
                        <figure className="relative h-48 overflow-hidden rounded-t-2xl">
                          <img
                            alt={restaurant.name || restaurant.title}
                            className="h-full w-full object-cover"
                            src={`${import.meta.env.VITE_API_STORAGE_URL}/storage/${restaurant.image}`}
                          />
                          {/* Price overlay in Rands */}
                          <div className="absolute right-2 top-2 z-10">
                            <span className="badge badge-accent badge-lg text-lg font-bold shadow-md">
                              R {restaurant.price}
                            </span>
                          </div>
                          {restaurant.tags && (
                            <div className="absolute left-2 top-2 flex gap-1">
                              {restaurant.tags.map(
                                (tag: string, tagIndex: number) => (
                                  <span
                                    className="badge badge-primary badge-sm"
                                    key={tagIndex}
                                  >
                                    {tag}
                                  </span>
                                ),
                              )}
                            </div>
                          )}
                        </figure>

                        <div className="card-body">
                          <h3 className="card-title text-lg font-semibold">
                            {restaurant.name || restaurant.title}
                          </h3>
                          <p className="mb-1 text-base-content/70">
                            <span className="font-semibold">Category:</span>{" "}
                            {restaurant.category}
                          </p>
                          <p className="mb-1 text-base-content/70">
                            <span className="font-semibold">Description:</span>{" "}
                            {restaurant.description}
                          </p>
                          <div className="mb-2 flex flex-wrap gap-2">
                            <span className="badge badge-info">
                              {restaurant.available
                                ? "Available"
                                : "Not Available"}
                            </span>
                            <span className="badge badge-warning">
                              {restaurant.popular ? "Popular" : "Not Popular"}
                            </span>
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-sm">
                            <div className="flex items-center gap-1">
                              <Star
                                className="fill-current text-warning"
                                size={16}
                              />
                              <span className="font-semibold">
                                {restaurant.rating ||
                                  restaurant.avg_rating ||
                                  "-"}
                              </span>
                            </div>
                            {restaurant.delivery_time && (
                              <>
                                <span>•</span>
                                <div className="flex items-center gap-1">
                                  <Clock size={16} />
                                  <span>{restaurant.delivery_time}</span>
                                </div>
                              </>
                            )}
                            {restaurant.delivery_fee && (
                              <>
                                <span>•</span>
                                <span>{restaurant.delivery_fee}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
                <div className="mt-8 flex justify-center gap-4">
                  <button
                    aria-label="Previous"
                    className="btn btn-outline flex items-center gap-2"
                    disabled={slideIndex === 0}
                    onClick={handlePrev}
                  >
                    <ChevronLeft size={20} />
                    <span className="hidden sm:inline">Back</span>
                  </button>
                  <button
                    aria-label="Next"
                    className="btn btn-outline flex items-center gap-2"
                    disabled={slideIndex === maxIndex}
                    onClick={handleNext}
                  >
                    <span className="hidden sm:inline">Next</span>
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Features */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">
                Why Choose Hareeng Eats?
              </h2>
            </div>

            <div className="grid gap-8 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
              {features.map((feature, index) => (
                <div className="text-center" key={index}>
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-base-200">
                    {feature.icon}
                  </div>
                  <h3 className="mb-2 text-xl font-semibold">
                    {feature.title}
                  </h3>
                  <p className="text-base-content/70">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* App Download */}
        <section
          className="py-12 transition-colors duration-300 sm:py-16"
          style={{
            backgroundColor: isDark ? "#111827" : "#ffffff",
            color: isDark ? "#ffffff" : "#111827",
          }}
        >
          <div className="mx-auto max-w-7xl px-4">
            <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
              <div className="relative flex min-h-[280px] items-center justify-center text-center sm:min-h-[320px] lg:min-h-[400px]">
                <img
                  alt={appDownloadContent?.imageAlt || "Mobile app interface"}
                  className="relative z-10 mx-auto h-64 w-auto max-w-[85%] rounded-2xl object-cover shadow-2xl sm:h-72 lg:h-96"
                  src={
                    appDownloadContent?.imageUrl ||
                    "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=300&h=400&fit=crop&crop=center"
                  }
                  style={{
                    boxShadow: "0 24px 64px 0 rgba(0,0,0,0.18)",
                    transform: "perspective(900px) rotateX(18deg) scale(1.1)",
                  }}
                />
                <div
                  className="absolute bottom-4 left-1/2 h-6 w-48 -translate-x-1/2 rounded-full bg-base-200 opacity-60 blur-md sm:bottom-6 sm:w-64 lg:w-72"
                  style={{
                    zIndex: 5,
                  }}
                ></div>
              </div>
              <div className="flex flex-col justify-center">
                <h2 className="mb-4 text-3xl font-bold sm:mb-6 sm:text-4xl">
                  {appDownloadContent?.heading || "Get the Hareeng Eats App"}
                </h2>
                <p className="mb-6 text-base opacity-90 sm:mb-8 sm:text-lg">
                  {appDownloadContent?.description ||
                    "Order faster, track in real-time, and unlock exclusive app-only deals."}
                </p>
                <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                  {appDownloadContent?.iosUrl && (
                    <button
                      className="btn btn-accent btn-lg sm:btn-lg"
                      onClick={() => setShowQR("ios")}
                    >
                      📱 Download for iOS
                    </button>
                  )}
                  {appDownloadContent?.androidUrl && (
                    <button
                      className="btn btn-outline btn-lg sm:btn-lg"
                      onClick={() => setShowQR("android")}
                    >
                      🤖 Download for Android
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          {showQR && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="relative flex w-full max-w-sm flex-col items-center rounded-3xl bg-white p-6 text-center shadow-2xl dark:bg-base-200 sm:p-8">
                <button
                  aria-label="Close"
                  className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-base-200 text-2xl font-bold text-gray-500 transition-all hover:bg-primary hover:text-white dark:bg-base-300"
                  onClick={() => setShowQR(null)}
                  type="button"
                >
                  ×
                </button>
                <h3 className="mb-4 mt-2 text-xl font-bold text-primary dark:text-accent sm:mb-6 sm:text-2xl">
                  Scan to Download {showQR === "ios" ? "iOS" : "Android"} App
                </h3>
                <div className="mb-4 flex items-center justify-center rounded-xl bg-base-200 p-3 dark:bg-base-100 sm:mb-6 sm:p-4">
                  <QRCodeCanvas
                    bgColor={isDark ? "#1e293b" : "#ffffff"}
                    fgColor={isDark ? "#f3f4f6" : "#1f2937"}
                    size={200}
                    style={{
                      background: isDark ? "#1e293b" : "#fff",
                      borderRadius: "12px",
                      boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
                      padding: "8px",
                    }}
                    value={
                      showQR === "ios"
                        ? appDownloadContent?.iosUrl || iosAppUrl
                        : appDownloadContent?.androidUrl || androidAppUrl
                    }
                  />
                </div>
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-300 sm:mb-6 sm:text-base">
                  Point your camera at the QR code above to download the app
                </p>
                <button
                  className="btn btn-primary btn-sm w-full sm:btn-md"
                  onClick={() => setShowQR(null)}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </>
  );
}
